import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useKeyboardShortcuts, defaultShortcuts } from '../hooks/useKeyboardShortcuts';
import { KeyboardShortcutsModal } from './shared/KeyboardShortcutsModal';
import { MobileNav } from './shared/MobileNav';
import { HelpCenter } from './shared/HelpCenter';
import { OnboardingTour } from './shared/OnboardingTour';
import {
  LayoutDashboard,
  Briefcase,
  User,
  Shield,
  MessageSquare,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Users,
  FileCheck,
  Repeat,
  Store,
  HelpCircle,
  FileText
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
  currentView: string;
  onViewChange: (view: string) => void;
}

export const Layout: React.FC<LayoutProps> = ({ children, currentView, onViewChange }) => {
  const { profile, signOut, switchRole } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showRoleSwitch, setShowRoleSwitch] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);

  const shortcuts = defaultShortcuts(onViewChange, profile?.role || 'contractor');
  useKeyboardShortcuts(shortcuts);

  const isContractor = profile?.role === 'contractor';

  const mobilePrimaryNavItems = isContractor
    ? [
        { id: 'dashboard', label: 'Home', icon: LayoutDashboard },
        { id: 'jobs', label: 'Jobs', icon: Briefcase },
        { id: 'marketplace', label: 'Market', icon: Store },
        { id: 'messages', label: 'Messages', icon: MessageSquare },
        { id: 'profile', label: 'Profile', icon: User },
      ]
    : [
        { id: 'dashboard', label: 'Home', icon: LayoutDashboard },
        { id: 'manage-jobs', label: 'Jobs', icon: Briefcase },
        { id: 'trades', label: 'Trades', icon: Users },
        { id: 'messages', label: 'Messages', icon: MessageSquare },
        { id: 'compliance', label: 'More', icon: FileCheck },
      ];

  const contractorNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'jobs', label: 'Find Jobs', icon: Briefcase },
    { id: 'applications', label: 'My Applications', icon: FileText },
    { id: 'marketplace', label: 'Marketplace', icon: Store },
    { id: 'insurance', label: 'Insurance', icon: Shield },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
  ];

  const gcNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'manage-jobs', label: 'Manage Jobs', icon: Briefcase },
    { id: 'trades', label: 'Find Trades', icon: Users },
    { id: 'marketplace', label: 'Marketplace', icon: Store },
    { id: 'compliance', label: 'Compliance', icon: FileCheck },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
  ];

  const navItems = isContractor ? contractorNavItems : gcNavItems;

  const handleSwitchRole = async () => {
    try {
      const newRole = isContractor ? 'gc' : 'contractor';
      await switchRole(newRole);
      setShowRoleSwitch(false);
      onViewChange('dashboard');
    } catch (error) {
      console.error('Error switching role:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className={`border-b transition-colors ${
        isContractor ? 'bg-blue-600 border-blue-700' : 'bg-amber-500 border-amber-600'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden p-2 rounded-lg ${
                  isContractor ? 'text-white hover:bg-blue-700' : 'text-white hover:bg-amber-600'
                }`}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h1 className={`text-xl font-bold ml-2 md:ml-0 text-white`}>
                VendorBoom
              </h1>
              <span className={`ml-3 px-2.5 py-1 rounded-full text-xs font-medium ${
                isContractor
                  ? 'bg-blue-700 text-white'
                  : 'bg-amber-600 text-white'
              }`}>
                {isContractor ? 'Contractor' : 'GC'}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <button className={`p-2 rounded-lg relative ${
                isContractor ? 'text-white hover:bg-blue-700' : 'text-white hover:bg-amber-600'
              }`}>
                <Bell className="w-5 h-5" />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>

              <button
                onClick={() => setShowHelpCenter(true)}
                className={`p-2 rounded-lg ${
                  isContractor ? 'text-white hover:bg-blue-700' : 'text-white hover:bg-amber-600'
                }`}
                title="Help Center"
              >
                <HelpCircle className="w-5 h-5" />
              </button>

              <button
                onClick={() => setShowRoleSwitch(!showRoleSwitch)}
                className={`p-2 rounded-lg ${
                  isContractor ? 'text-white hover:bg-blue-700' : 'text-white hover:bg-amber-600'
                }`}
                title="Switch role"
              >
                <Repeat className="w-5 h-5" />
              </button>

              <button
                onClick={() => onViewChange('settings')}
                className={`p-2 rounded-lg ${
                  isContractor ? 'text-white hover:bg-blue-700' : 'text-white hover:bg-amber-600'
                }`}
              >
                <Settings className="w-5 h-5" />
              </button>

              <button
                onClick={signOut}
                className={`p-2 rounded-lg ${
                  isContractor ? 'text-white hover:bg-blue-700' : 'text-white hover:bg-amber-600'
                }`}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {showRoleSwitch && (
          <div className="absolute right-4 top-16 mt-2 bg-white rounded-xl shadow-lg border border-slate-200 p-4 z-50 w-72">
            <h3 className="font-semibold text-slate-900 mb-2">Switch Role</h3>
            <p className="text-sm text-slate-600 mb-4">
              Change to {isContractor ? 'General Contractor' : 'Contractor'} view
            </p>
            <button
              onClick={handleSwitchRole}
              className={`w-full font-medium py-2 rounded-lg transition-colors text-white ${
                isContractor ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              Switch to {isContractor ? 'GC' : 'Contractor'} Mode
            </button>
            <button
              onClick={() => setShowRoleSwitch(false)}
              className="w-full mt-2 text-slate-600 hover:text-slate-900 font-medium py-2 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </header>

      <div className="flex">
        <aside className={`${
          mobileMenuOpen ? 'block' : 'hidden'
        } md:block w-64 flex-shrink-0 border-r bg-white border-slate-200 min-h-[calc(100vh-4rem)] fixed md:sticky top-16 z-40 md:z-auto`}>
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onViewChange(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center px-4 py-3 rounded-lg text-left transition-all ${
                    isActive
                      ? isContractor
                        ? 'bg-blue-600 text-white'
                        : 'bg-amber-500 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span className="font-medium whitespace-nowrap">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 pb-20 md:pb-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      <MobileNav
        items={mobilePrimaryNavItems}
        activeId={currentView}
        onItemClick={(id) => {
          onViewChange(id);
          setMobileMenuOpen(false);
        }}
      />

      <KeyboardShortcutsModal shortcuts={shortcuts} />
      <HelpCenter isOpen={showHelpCenter} onClose={() => setShowHelpCenter(false)} />
      <OnboardingTour view={currentView} />
    </div>
  );
};
